import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { EmptyState } from '@components/common/EmptyState';
import { PageHeader } from '@components/common/PageHeader';
import { SectionCard } from '@components/common/SectionCard';
import { useApolloTemplates, useCRMSettings } from '@hooks/useCRM';
import { formatDate, formatRelativeDate } from '@lib/format';
import type { CRMApolloTemplateOption } from '@/types/api';

export function TemplatesPage() {
  const [search, setSearch] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const { data: settings } = useCRMSettings();
  const apolloReady = Boolean(settings?.apollo.enabled && settings?.apollo.has_api_key);
  const { data: templates, isLoading, error } = useApolloTemplates(search || undefined, apolloReady);

  const selectedTemplate = useMemo(
    () => templates?.find((template) => template.id === selectedTemplateId) ?? templates?.[0] ?? null,
    [selectedTemplateId, templates],
  );

  return (
    <div className="space-y-8">
      <PageHeader
        title="Apollo Templates"
        subtitle="Apollo owns sequence email template logic. CRM reads live Apollo templates and step content for inspection only."
      />

      <div className="split-layout">
        <SectionCard
          title="Template Library"
          subtitle="Sequence-linked templates pulled directly from Apollo."
          action={(
            <label className="search-field">
              <Search size={16} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search Apollo templates"
              />
            </label>
          )}
        >
          {!apolloReady ? (
            <EmptyState
              title="Apollo is not configured"
              body="Enable Apollo and set APOLLO_API_KEY before browsing templates."
            />
          ) : isLoading ? (
            <div className="loading-row">Loading Apollo templates…</div>
          ) : error ? (
            <p className="form-error">{error instanceof Error ? error.message : 'Failed to load Apollo templates.'}</p>
          ) : templates?.length ? (
            <div className="stack-list">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className="stack-item stack-item-block"
                  onClick={() => setSelectedTemplateId(template.id)}
                >
                  <div>
                    <h4>{template.name || 'Unnamed Apollo template'}</h4>
                    <span className="pill pill-soft">{template.step_type || 'email'}</span>
                  </div>
                  <p>{template.sequence_name}</p>
                  <p>{template.status || 'status n/a'}</p>
                  <p>{template.updated_at ? formatRelativeDate(template.updated_at) : 'no update timestamp'}</p>
                </button>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No Apollo templates found"
              body="Apollo did not return template-like sequence steps for this query."
            />
          )}
        </SectionCard>

        <SectionCard
          title="Template Inspector"
          subtitle="Review sequence linkage and content exactly as Apollo reports it."
        >
          {!selectedTemplate ? (
            <EmptyState
              title="No template selected"
              body="Select an Apollo template from the left panel to inspect its metadata and copy."
            />
          ) : (
            <ApolloTemplateInspector template={selectedTemplate} />
          )}
        </SectionCard>
      </div>
    </div>
  );
}

type ApolloTemplateInspectorProps = {
  template: CRMApolloTemplateOption;
};

function ApolloTemplateInspector({ template }: ApolloTemplateInspectorProps) {
  return (
    <div className="detail-stack">
      <div className="apollo-inspector-section">
        <h4>Template Metadata</h4>
        <dl className="apollo-inspector-meta">
          <div>
            <dt>Template ID</dt>
            <dd>{template.id}</dd>
          </div>
          <div>
            <dt>Sequence</dt>
            <dd>{template.sequence_name}</dd>
          </div>
          <div>
            <dt>Sequence ID</dt>
            <dd>{template.sequence_id}</dd>
          </div>
          <div>
            <dt>Step Type</dt>
            <dd>{template.step_type || 'email'}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{template.status || '—'}</dd>
          </div>
          <div>
            <dt>Owner</dt>
            <dd>{template.owner_name || '—'}</dd>
          </div>
          <div>
            <dt>Updated</dt>
            <dd>{formatDate(template.updated_at) || '—'}</dd>
          </div>
        </dl>
      </div>

      <div className="apollo-inspector-section">
        <h4>Subject</h4>
        <p>{template.subject || 'No subject provided by Apollo for this step.'}</p>
      </div>

      <div className="apollo-inspector-section">
        <h4>Body</h4>
        <pre>{template.body || 'No body provided by Apollo for this step.'}</pre>
      </div>
    </div>
  );
}
